import React, { useState } from "react";
import { Plus, Trash2, Pencil, Check, X, Tag, List, Type as TypeIcon } from "lucide-react";
import { toast } from "sonner";
import * as api from "../../lib/api";
import { useApp } from "../../context/AppContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { ICON_NAMES, iconByName } from "./categoryIcons";

const put = (id, data) => api.default.put(`/inventory/categories/${id}`, data);
const suggestCode = (label) => label.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 2);
let _suggestTimer = null;
const err = (e) => toast.error(e.response?.data?.detail || "Erreur");

function IconPicker({ value, onChange, testId }) {
    return (
        <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto p-1 border rounded-md bg-gray-50" data-testid={testId}>
            {ICON_NAMES.map((n) => {
                const I = iconByName(n);
                return (
                    <button key={n} type="button" onClick={() => onChange(n)} title={n} data-testid={`${testId}-${n}`}
                        className={`w-7 h-7 inline-flex items-center justify-center rounded border ${value === n ? "bg-gray-900 text-white border-gray-900" : "bg-white border-gray-200 hover:border-gray-400"}`}>
                        <I className="w-3.5 h-3.5" />
                    </button>
                );
            })}
        </div>
    );
}

function NewCategoryForm({ onCreated }) {
    const [open, setOpen] = useState(false);
    const [label, setLabel] = useState("");
    const [code, setCode] = useState("");
    const [codeTouched, setCodeTouched] = useState(false);
    const [icon, setIcon] = useState("Package");
    const [saving, setSaving] = useState(false);

    const reset = () => { setLabel(""); setCode(""); setCodeTouched(false); setIcon("Package"); setOpen(false); };
    const submit = async () => {
        if (!label.trim()) { toast.error("Libellé requis"); return; }
        setSaving(true);
        try {
            await api.default.post("/inventory/categories", { label: label.trim(), tag_code: code.trim().toUpperCase(), icon });
            toast.success(`Catégorie « ${label.trim()} » créée`);
            reset();
            onCreated();
        } catch (e) { err(e); } finally { setSaving(false); }
    };

    if (!open) return <Button onClick={() => setOpen(true)} data-testid="new-category-btn"><Plus className="w-4 h-4 mr-1" />Nouvelle catégorie</Button>;
    const Preview = iconByName(icon);
    return (
        <div className="border-2 border-dashed border-teal-300 rounded-lg p-3 space-y-3 bg-teal-50/30" data-testid="new-category-form">
            <div className="grid grid-cols-[1fr_110px] gap-3">
                <div>
                    <label className="form-label text-xs">Libellé *</label>
                    <Input autoFocus value={label} onChange={(e) => {
                        const v = e.target.value; setLabel(v);
                        if (codeTouched) return;
                        setCode(suggestCode(v));
                        clearTimeout(_suggestTimer);
                        if (v.trim()) _suggestTimer = setTimeout(() => api.default.get("/inventory/categories/suggest-code", { params: { label: v.trim() } }).then((r) => setCode((c) => (codeTouched ? c : r.data.tag_code))).catch(() => {}), 350);
                    }} placeholder="ex. Caméra, Clavier, Écran tactile…" data-testid="new-category-label" />
                </div>
                <div>
                    <label className="form-label text-xs">Code Asset Tag</label>
                    <Input value={code} onChange={(e) => { setCodeTouched(true); setCode(e.target.value.toUpperCase()); }} maxLength={4} className="uppercase font-mono" placeholder="XX" data-testid="new-category-code" />
                </div>
            </div>
            <div>
                <label className="form-label text-xs flex items-center gap-2">Icône <Preview className="w-3.5 h-3.5 text-gray-600" /></label>
                <IconPicker value={icon} onChange={setIcon} testId="new-category-icon" />
            </div>
            <p className="text-[10px] text-gray-500 font-mono">Aperçu Asset Tag : EGG-{code || "XX"}-42317</p>
            <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={reset} data-testid="new-category-cancel">Annuler</Button>
                <Button size="sm" onClick={submit} disabled={saving} data-testid="new-category-save">{saving ? "Création…" : "Créer la catégorie"}</Button>
            </div>
        </div>
    );
}

// Édition des options d'un champ « liste déroulante »
function FieldOptionsEditor({ field, onSave, onClose, testPrefix }) {
    const [options, setOptions] = useState(field.options || []);
    const [newOpt, setNewOpt] = useState("");
    const add = () => {
        const v = newOpt.trim();
        if (!v || options.includes(v)) { setNewOpt(""); return; }
        setOptions([...options, v]);
        setNewOpt("");
    };
    return (
        <div className="mt-2 border rounded-md p-2 bg-gray-50 space-y-2" data-testid={`${testPrefix}-options-editor`}>
            <p className="text-[11px] font-medium text-gray-600">Options de « {field.label} »</p>
            <div className="flex flex-wrap gap-1.5">
                {options.map((o) => (
                    <span key={o} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-white border border-gray-200 text-gray-700" data-testid={`${testPrefix}-option-${o}`}>
                        {o}<button type="button" onClick={() => setOptions(options.filter((x) => x !== o))} className="text-gray-400 hover:text-red-500" title="Retirer">&times;</button>
                    </span>
                ))}
                {options.length === 0 && <span className="text-[10px] text-gray-400">Aucune option — ajoutez-en ci-dessous</span>}
            </div>
            <div className="flex items-center gap-2">
                <Input value={newOpt} onChange={(e) => setNewOpt(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} placeholder="Nouvelle option (ex. Stand écran)" className="flex-1 h-8 text-xs" data-testid={`${testPrefix}-new-option`} />
                <Button size="sm" variant="outline" onClick={add} data-testid={`${testPrefix}-add-option`}><Plus className="w-3.5 h-3.5" /></Button>
            </div>
            <div className="flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={onClose}>Annuler</Button>
                <Button size="sm" onClick={() => onSave(options)} data-testid={`${testPrefix}-save-options`}>Enregistrer les options</Button>
            </div>
        </div>
    );
}

function CategoryRow({ cat, onChanged, isAdmin }) {
    const [editing, setEditing] = useState(false);
    const [label, setLabel] = useState(cat.label);
    const [icon, setIcon] = useState(cat.icon || "Package");
    const [fieldsOpen, setFieldsOpen] = useState(false);
    const [newField, setNewField] = useState("");
    const [newFieldType, setNewFieldType] = useState("text");
    const [editingField, setEditingField] = useState(null);
    const Icon = iconByName(cat.icon);

    const saveMeta = async () => {
        try { await put(cat.id, { label: label.trim(), icon }); toast.success("Catégorie mise à jour"); setEditing(false); onChanged(); } catch (e) { err(e); }
    };
    const saveTagCode = async (value) => {
        const c = value.trim().toUpperCase();
        if ((cat.tag_code || "") === c) return;
        try { await put(cat.id, { tag_code: c }); toast.success("Code Asset Tag mis à jour"); onChanged(); } catch (e) { err(e); }
    };
    const addField = async () => {
        if (!newField.trim()) return;
        const key = newField.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]/g, "_").slice(0, 30);
        if ((cat.custom_fields || []).some((f) => f.key === key)) { toast.error("Un champ avec ce nom existe déjà"); return; }
        const field = newFieldType === "select" ? { key, label: newField.trim(), type: "select", options: [] } : { key, label: newField.trim(), type: "text" };
        try {
            await put(cat.id, { custom_fields: [...(cat.custom_fields || []), field] });
            toast.success(newFieldType === "select" ? "Liste ajoutée — ajoutez maintenant ses options" : "Champ ajouté");
            setNewField(""); setNewFieldType("text");
            if (newFieldType === "select") setEditingField(key);
            onChanged();
        } catch (e) { err(e); }
    };
    const saveOptions = async (fieldKey, options) => {
        try {
            await put(cat.id, { custom_fields: (cat.custom_fields || []).map((f) => (f.key === fieldKey ? { ...f, options } : f)) });
            toast.success("Options enregistrées"); setEditingField(null); onChanged();
        } catch (e) { err(e); }
    };
    const removeField = async (k) => {
        try { await put(cat.id, { custom_fields: (cat.custom_fields || []).filter((f) => f.key !== k) }); toast.success("Champ supprimé"); onChanged(); } catch (e) { err(e); }
    };
    const remove = async () => {
        if (!window.confirm(`Supprimer la catégorie « ${cat.label} » ?`)) return;
        try { const r = await api.default.delete(`/inventory/categories/${cat.id}`); toast.success(r.data?.message || "Supprimée"); onChanged(); } catch (e) { err(e); }
    };

    return (
        <div className="border rounded-lg p-3" data-testid={`settings-cat-${cat.key}`}>
            <div className="flex items-center gap-2 mb-2">
                <span className="w-7 h-7 rounded-md bg-gray-100 inline-flex items-center justify-center text-gray-700"><Icon className="w-4 h-4" /></span>
                {editing ? (
                    <Input value={label} onChange={(e) => setLabel(e.target.value)} className="h-7 text-sm flex-1" data-testid={`cat-label-input-${cat.key}`} />
                ) : (
                    <h4 className="text-sm font-semibold text-gray-800 flex-1 truncate">{cat.label}
                        {cat.is_custom && <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded-full bg-teal-50 text-teal-700 border border-teal-100 font-medium align-middle">Personnalisée</span>}
                        <span className="ml-2 text-[10px] text-gray-400 font-normal">{cat.items_count ?? 0} item{(cat.items_count ?? 0) > 1 ? "s" : ""}</span>
                    </h4>
                )}
                {editing ? (
                    <>
                        <Button size="sm" variant="ghost" onClick={saveMeta} title="Enregistrer" data-testid={`cat-save-${cat.key}`}><Check className="w-3.5 h-3.5 text-emerald-600" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setLabel(cat.label); setIcon(cat.icon || "Package"); }} title="Annuler"><X className="w-3.5 h-3.5" /></Button>
                    </>
                ) : (
                    <>
                        <Button size="sm" variant="ghost" onClick={() => setEditing(true)} title="Renommer / changer l'icône" data-testid={`cat-edit-${cat.key}`}><Pencil className="w-3.5 h-3.5" /></Button>
                        {isAdmin && <Button size="sm" variant="ghost" onClick={remove} disabled={(cat.items_count ?? 0) > 0} title={(cat.items_count ?? 0) > 0 ? "Des items utilisent cette catégorie" : "Supprimer"} data-testid={`cat-delete-${cat.key}`}><Trash2 className={`w-3.5 h-3.5 ${(cat.items_count ?? 0) > 0 ? "text-gray-300" : "text-red-500"}`} /></Button>}
                    </>
                )}
            </div>
            {editing && <div className="mb-2"><IconPicker value={icon} onChange={setIcon} testId={`cat-icon-${cat.key}`} /></div>}
            <div className="flex items-center gap-2 mb-2">
                <Tag className="w-3 h-3 text-gray-400" />
                <span className="text-[10px] text-gray-500 whitespace-nowrap">Code Asset Tag :</span>
                <Input defaultValue={cat.tag_code || ""} placeholder={cat.effective_tag_code || "XX"} maxLength={4} className="w-20 h-7 text-xs uppercase font-mono" onBlur={(e) => saveTagCode(e.target.value)} data-testid={`tag-code-${cat.key}`} />
                <span className="text-[10px] text-gray-400 font-mono">EGG-{cat.tag_code || cat.effective_tag_code || "XX"}-42317</span>
                <button onClick={() => setFieldsOpen(!fieldsOpen)} className="ml-auto text-xs text-teal-600 hover:underline" data-testid={`cat-fields-toggle-${cat.key}`}>
                    {fieldsOpen ? "Fermer" : "+ Ajouter un champ"}
                </button>
            </div>
            <div className="flex flex-wrap gap-1.5">
                {(cat.custom_fields || []).map((f) => (
                    <span key={f.key} className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${f.type === "select" ? "bg-teal-50 text-teal-800 border border-teal-100" : "bg-gray-100 text-gray-700"}`} data-testid={`field-chip-${cat.key}-${f.key}`}>
                        {f.type === "select" ? <List className="w-3 h-3" /> : <TypeIcon className="w-3 h-3 text-gray-400" />}
                        {f.label}
                        {f.type === "select" && (
                            <button type="button" onClick={() => setEditingField(editingField === f.key ? null : f.key)} className="text-teal-600 hover:underline ml-0.5" title="Gérer les options de la liste" data-testid={`field-options-${cat.key}-${f.key}`}>
                                {(f.options || []).length} option{(f.options || []).length > 1 ? "s" : ""}
                            </button>
                        )}
                        <button onClick={() => removeField(f.key)} className="text-gray-400 hover:text-red-500 ml-0.5" title="Supprimer le champ">&times;</button>
                    </span>
                ))}
                {(!cat.custom_fields || cat.custom_fields.length === 0) && <span className="text-[10px] text-gray-400">Aucun champ de spécification</span>}
            </div>
            {editingField && (cat.custom_fields || []).find((f) => f.key === editingField) && (
                <FieldOptionsEditor key={editingField} field={(cat.custom_fields || []).find((f) => f.key === editingField)} onSave={(opts) => saveOptions(editingField, opts)} onClose={() => setEditingField(null)} testPrefix={`${cat.key}-${editingField}`} />
            )}
            {fieldsOpen && (
                <div className="flex items-center gap-2 mt-2">
                    <Input value={newField} onChange={(e) => setNewField(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addField()} placeholder="Nom du champ (ex. Type, Résolution…)" className="flex-1 text-xs" data-testid={`new-field-label-${cat.key}`} />
                    <select value={newFieldType} onChange={(e) => setNewFieldType(e.target.value)} className="h-9 px-2 text-xs border rounded-md bg-white" title="Type de champ" data-testid={`new-field-type-${cat.key}`}>
                        <option value="text">Texte libre</option>
                        <option value="select">Liste déroulante</option>
                    </select>
                    <Button size="sm" onClick={addField} data-testid={`add-field-${cat.key}`}>Ajouter</Button>
                </div>
            )}
        </div>
    );
}

// Paramètres des catégories d'inventaire : création, libellé, icône, code Asset Tag, champs de spécification, suppression
export function CategorySettingsDialog({ open, onClose, categories, onChanged }) {
    const { hasPermission } = useApp();
    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="category-settings-dialog">
                <DialogHeader><DialogTitle>Paramètres — Catégories d'inventaire</DialogTitle></DialogHeader>
                <p className="text-xs text-gray-500">Chaque catégorie dispose de son code Asset Tag (EGG-CODE-XXXXX), de son icône et de ses champs de spécification. Les catégories que vous créez fonctionnent exactement comme les catégories natives (étiquettes QR, documents PDF, filtres, templates).</p>
                <NewCategoryForm onCreated={onChanged} />
                <div className="space-y-3">
                    {categories.map((cat) => <CategoryRow key={cat.id} cat={cat} onChanged={onChanged} isAdmin={hasPermission("admin")} />)}
                </div>
                <DialogFooter><Button variant="outline" onClick={onClose} data-testid="category-settings-close">Fermer</Button></DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
